import { useAppSelector } from '@/reducers';
import { FieldArray, Form, Formik, FormikProps } from 'formik';
import { isEmpty } from 'lodash';
import { Fragment, FC, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { CrmField, CrmFields } from '../../../../types';
import { StepPageActions } from '@/global/components';
import { useWorkspace } from '@/features/workspaces/hooks';
import { CREATE_DATA_SOURCE_CRM } from '../constants';
import { Checkbox } from '@/components/inputs';
import { stashFields } from '@/features/cyclr/dataSourceIntegrationSlice';
import { stashDataSource } from '@/features/dataSources/dataSourcesSlice';
import { useUpdateCrmDataSourceFieldsMutation } from '@/services/dataSources';
import { DataSource, DataSourceCrmConfig } from '@/models/dataSource';

interface ICrmDataSourceFieldsFormProps {
  onNextStep?: VoidFunction;
  onPrevStep?: VoidFunction;
  onGoToStep?: (id: CREATE_DATA_SOURCE_CRM) => void;
  onCancel?: VoidFunction;
}

export const CrmDataSourceFieldsStep: FC<ICrmDataSourceFieldsFormProps> = ({ onNextStep, onPrevStep }) => {
  const dispatch = useDispatch();
  const workspace = useWorkspace();

  const crmFieldsFormRef = useRef<FormikProps<CrmFields>>(null);

  const dataSource = useAppSelector((state) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceCrmConfig>> | undefined;
  const fieldDefs = useAppSelector((state) => state.dataSourceIntegration.fieldDefinitions);
  const storedFields = useAppSelector((state) => state.dataSourceIntegration.fields);
  const storedFieldMap = new Map<number, CrmField>(storedFields.map((field) => [field.fieldId, field]));

  const fields: CrmField[] = fieldDefs.map((fieldDef) => ({
    ...fieldDef,
    checked: storedFieldMap.has(fieldDef.fieldId) ? (storedFieldMap.get(fieldDef.fieldId) as CrmField).checked : true,
  }));

  const [updateFields, { data: updateFieldsResponse, isLoading: isUpdateFieldsLoading }] = useUpdateCrmDataSourceFieldsMutation();

  useEffect(() => {
    if (updateFieldsResponse) {
      dispatch(stashDataSource(updateFieldsResponse.dataSource));
      onNextStep();
    }
  }, [updateFieldsResponse]);

  const handleSubmit = () => {
    console.debug('On fields submit');
  };

  const submitFields = (values: CrmFields) => {
    if (dataSource && dataSource.id) {
      updateFields({
        workspaceId: workspace.id,
        dataSourceId: dataSource.id,
        fields: values.fields
          .filter((field) => !field.checked)
          .map((field) => ({
            stepId: field.stepId,
            fieldId: field.fieldId,
            mappingType: 'Ignore',
            value: field.value,
          })),
      });
    }
  };

  const handleNextStep = async () => {
    await crmFieldsFormRef.current?.submitForm();

    const values = crmFieldsFormRef.current?.values;
    if (crmFieldsFormRef.current?.isValid && values) {
      dispatch(stashFields(values.fields));

      if (values.fields.every((field) => field.checked)) {
        onNextStep();
      } else {
        submitFields(values);
      }
    }
  };

  return (
    <>
      <Formik innerRef={crmFieldsFormRef} initialValues={{ fields }} validateOnBlur={false} validateOnChange={false} onSubmit={handleSubmit}>
        {({ values, setFieldValue }) => {
          return (
            <Form>
              <FieldArray
                name='fields'
                validateOnChange={false}
                render={(clickEventHelper) => (
                  <Fragment>
                    {clickEventHelper &&
                      !isEmpty(clickEventHelper) &&
                      values.fields.map((field, index) => (
                        <Checkbox
                          key={field.fieldId}
                          label={field.name}
                          description={field.description}
                          disabled={!field.isOptional}
                          checked={field.checked}
                          onChange={(e) => setFieldValue(`fields.${index}`, { ...field, checked: e.target.checked })}
                        />
                      ))}
                  </Fragment>
                )}
              />
            </Form>
          );
        }}
      </Formik>

      <StepPageActions
        primaryButton={{
          text: 'COMMON.confirm',
          onClick: handleNextStep,
          isLoading: isUpdateFieldsLoading,
        }}
        secondaryButton={{
          text: 'COMMON.cancel',
          onClick: onPrevStep,
        }}
      />
    </>
  );
};
